import { INTAKE_STEP_LABELS } from "@/onboarding/routes";
import {
  EMAIL_INVOICE_SOURCES,
  FORWARDING_CHECK_STATE,
  SERVICE_TITAN_STAGE,
  ST_ACCESS_METHOD,
  type BusinessOnboarding,
  type IntakeStep,
} from "./types";

/**
 * A task is never only ticked or not. "Done" and "not started" leave no room for the
 * cases that actually need a person: forwarding the customer skipped, a ServiceTitan
 * tenant they never gave us. Those get their own state so they read as unfinished
 * business rather than as progress.
 */
export const TASK_STATE = {
  done: "done",
  attention: "attention",
  todo: "todo",
} as const;

export type TaskState = (typeof TASK_STATE)[keyof typeof TASK_STATE];

export interface OnboardingTask {
  step: IntakeStep;
  label: string;
  description: string;
  state: TaskState;
  /** What the customer told us, in their terms. Shown instead of the word "done". */
  value: string;
}

const DESCRIPTIONS: Partial<Record<IntakeStep, string>> = {
  company: "Your company name and time zone.",
  schedule: "How often you pay, and when a pay period starts.",
  reference_data: "The numbers behind your last four pay runs.",
  pay_plan: "The rules you pay your team by.",
  invoice_sources: "Tell us where your vendor invoices come from.",
  forwarding_setup: "Get your invoices arriving at Volca.",
  invoice_upload: "Share recent invoices so we can learn your vendors.",
  st_access: "Connect Volca to your ServiceTitan account.",
  onboarding_call: "Check your information and submit it to Volca.",
};

const sourceCount = (state: BusinessOnboarding) =>
  state.answers.invoice_sources?.length ?? 0;

const forwardingTask = (state: BusinessOnboarding): [TaskState, string] => {
  const sources = state.answers.invoice_sources ?? [];
  const byEmail = sources.some((source) =>
    EMAIL_INVOICE_SOURCES.includes(source),
  );
  const walked = Boolean(
    state.step_progress.accounts_payable?.forwarding_setup,
  );
  if (state.answers.forwarding_skipped)
    return [TASK_STATE.attention, "Skipped for now"];
  // They told us nothing arrives by email, so there is no rule to make. Say so before
  // they open it, rather than letting the row read "Not started" as though work is
  // waiting: the step still exists, because the welcome screen lists every task up
  // front, but all it holds for them is how to get the other sources to us.
  if (!byEmail && sources.length)
    return walked
      ? [TASK_STATE.done, "Nothing to forward"]
      : [TASK_STATE.todo, "Nothing to forward"];
  if (state.forwarding_check.state === FORWARDING_CHECK_STATE.confirmed)
    return [TASK_STATE.done, "Forwarding working"];
  if (state.forwarding_check.state === FORWARDING_CHECK_STATE.waiting)
    return [TASK_STATE.todo, "Waiting for the test"];
  if (state.forwarding_check.state === FORWARDING_CHECK_STATE.timedOut)
    return [TASK_STATE.attention, "Test has not arrived"];
  // Walked the step and gave us an address, but never sent the test. The step is
  // finished as far as the API is concerned, so "Not started" is simply wrong. It is
  // outstanding business, which is what attention is for.
  if (walked) return [TASK_STATE.attention, "Test not sent"];
  return [TASK_STATE.todo, "Not started"];
};

// Invoices arrive by upload or by email, and forwarding takes as long as it takes, so
// the step does not block on one having arrived. Walking it with nothing here is not
// finished business, it is a task still waiting on the post.
const invoiceUploadTask = (state: BusinessOnboarding): [TaskState, string] => {
  const count = state.invoice_samples.length;
  if (count)
    return [
      TASK_STATE.done,
      `${count} ${count === 1 ? "invoice" : "invoices"} shared`,
    ];
  return state.step_progress.accounts_payable?.invoice_upload
    ? [TASK_STATE.attention, "Waiting for your first invoice"]
    : [TASK_STATE.todo, "Not started"];
};

// An administrator user owes us nothing: ServiceTitan emails Volca the invitation, and
// Volca then reads the tenant and sets up the API access itself. A limited user owes us a
// tenant ID, and has to approve our request at the end. ServiceTitan is a shared step, so
// its progress sits in the shared group rather than under accounts payable.
const stAccessTask = (state: BusinessOnboarding): [TaskState, string] => {
  const tenantId = state.service_titan_tenant_id;
  const method = state.answers.st_access_method;
  const done = state.step_progress.shared?.st_access;
  if (done && method === ST_ACCESS_METHOD.noServiceTitan)
    return [TASK_STATE.done, "Not used"];
  if (done && method === ST_ACCESS_METHOD.adminUser)
    return [TASK_STATE.done, "Admin user"];
  if (done && method === ST_ACCESS_METHOD.scopedUser && tenantId)
    return state.service_titan_stage === SERVICE_TITAN_STAGE.awaitingAcceptance
      ? [TASK_STATE.attention, "Approve our request in ServiceTitan"]
      : [TASK_STATE.done, `Tenant ${tenantId}, limited user`];
  if (done && method === ST_ACCESS_METHOD.scopedUser)
    return [TASK_STATE.attention, "Tenant ID needed"];
  if (!method && tenantId)
    return [TASK_STATE.attention, "Confirm the user and invite"];
  return [TASK_STATE.todo, method ? "In progress" : "Not started"];
};

const payrollTask = (
  state: BusinessOnboarding,
  step: IntakeStep,
): [TaskState, string] =>
  state.step_progress.payroll?.[step]
    ? [TASK_STATE.done, "Done"]
    : [TASK_STATE.todo, "Not started"];

const sharedTask = (
  state: BusinessOnboarding,
  step: IntakeStep,
): [TaskState, string] =>
  state.step_progress.shared?.[step]
    ? [TASK_STATE.done, "Done"]
    : [TASK_STATE.todo, "Not started"];

const taskState = (
  state: BusinessOnboarding,
  step: IntakeStep,
): [TaskState, string] => {
  switch (step) {
    case "invoice_sources": {
      const count = sourceCount(state);
      if (count && state.step_progress.accounts_payable?.invoice_sources)
        return [
          TASK_STATE.done,
          `${count} ${count === 1 ? "source" : "sources"}`,
        ];
      return [TASK_STATE.todo, count ? "In progress" : "Not started"];
    }
    case "forwarding_setup":
      return forwardingTask(state);
    case "invoice_upload":
      return invoiceUploadTask(state);
    case "st_access":
      return stAccessTask(state);
    case "onboarding_call":
      return state.submitted_at
        ? [TASK_STATE.done, "Submitted"]
        : [TASK_STATE.todo, "Ready for review"];
    case "schedule":
    case "reference_data":
    case "pay_plan":
      return payrollTask(state, step);
    default:
      return sharedTask(state, step);
  }
};

/** Welcome and waiting frame the list rather than belonging to it. */
export const onboardingTasks = (state: BusinessOnboarding): OnboardingTask[] =>
  state.steps
    .filter((step) => step !== "welcome" && step !== "waiting")
    .map((step) => {
      const [taskStatus, value] = taskState(state, step);
      return {
        step,
        label: INTAKE_STEP_LABELS[step],
        description: DESCRIPTIONS[step] ?? "",
        state: taskStatus,
        value,
      };
    });

export const tasksDone = (tasks: OnboardingTask[]) =>
  tasks.filter((task) => task.state === TASK_STATE.done).length;

/** Where "Continue where you left off" goes: the first task not finished. */
export const firstOpenTask = (tasks: OnboardingTask[]) =>
  tasks.find((task) => task.state !== TASK_STATE.done) ?? tasks[0];
